<template lang="pug">
ValidationProvider(
  ref="inputProvider"
  v-slot="{ errors }"
  mode="lazy"
  rules="required|time"
  :name="name"
  :model-value="modelCompatValue"
)
  .vue-app-time-input.time_entry.form-group(:class="{ 'has-error': errors[0], disabled }")
    AppTimePicker(
      :time="modelCompatValue"
      :type="type"
      :ceiling="ceiling"
      :floor="floor"
      :show-yesterday="showYesterday"
      @select="onChange"
      @toggle-visibility="onToggleVisibility"
    )
      template(#trigger)
        i.hi.hi-time.hi-16.app-time-picker-trigger

    input.form-control.default-input(
      :value="inputValue"
      :disabled="disabled"
      @keyup.up="onUp"
      @keydown.down="onDown"
      @input="onInput"
      @change="onChange"
    )

    span.meridiem-toggle(
      v-if="showMeridiem"
      :class="{ compact: popover }"
      @click="onMeridiemClick"
    )
      span(v-if="isAM") am
      span(v-else) pm

    span.help-block
      | {{ errors[0] }}
    slot
</template>

<script>
import { extend } from '~/utils/vee_validate_compat';
import { formatTime } from '~/utils/time_utils';
import VModelCompat from '~/mixins/v_model_compat';

extend('time', {
  validate: (val) => {
    const parsedTime = moment.isMoment(val) ? val : TimeInput.parse_time(val);

    return !!parsedTime;
  },
  message: 'Enter a time',
});

export default {
  name: 'AppTimeInput',
  mixins: [VModelCompat],
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    ceiling: {
      type: Object,
    },
    floor: {
      type: Object,
    },
    type: {
      type: String,
      default: 'primary',
    },
    showYesterday: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'app_time_input',
    },
    showSeconds: {
      type: String,
      default: 'auto',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showMeridiem: true,
    inputValue: '',
    isAM: true,
  }),
  computed: {
    is24HourFormat() {
      return /24/.test(TimeUtils.UserTimeFormats.time);
    },
    halfDay() {
      return moment.duration(12, 'hour');
    },
    popover() {
      return this.type === 'popover';
    },
  },
  watch: {
    modelCompatValue(val) {
      this.$refs.inputProvider.validate(val);
      this.resetMeridiem();
    },
  },
  created() {
    this.resetMeridiem();
  },
  methods: {
    onToggleVisibility(visible = true) {
      this.$emit(visible ? 'open' : 'close');
    },
    setInputValue(val) {
      this.inputValue = formatTime(val, {
        showSeconds: this.showSeconds,
        omitMeridiem: true,
      });
    },
    parseTime(val) {
      return moment.isMoment(val) ? val : TimeInput.parse_time(val);
    },
    onUp() {
      if (this.disabled) {
        return;
      }
      this.emitModelCompatValue(this.modelCompatValue.clone().add(1, 'minutes'));
    },
    onDown() {
      if (this.disabled) {
        return;
      }
      this.emitModelCompatValue(this.modelCompatValue.clone().subtract(1, 'minutes'));
    },
    onMeridiemClick() {
      if (this.disabled) {
        return;
      }
      const value = this.modelCompatValue.clone();

      this.isAM ? value.add(this.halfDay) : value.subtract(this.halfDay);
      this.emitModelCompatValue(value);
    },
    resetMeridiem() {
      this.showMeridiem = !this.is24HourFormat;
      this.setInputValue(this.modelCompatValue);
      this.setMeridiemFromTime();
    },
    matchMeridiem(string) {
      return string.match(/[ap][m]?/i);
    },
    matchAM(string) {
      return string.match(/[a][m]?/i);
    },
    is12based(time) {
      return time.format('hh') === '12';
    },
    timeIsAM(time) {
      return time.format('A') === 'AM';
    },
    setMeridiemFromTime() {
      this.isAM = this.timeIsAM(this.modelCompatValue);
    },
    setAMOnMatch(val) {
      const matches = this.matchMeridiem(val);

      if (matches && val.length <= 2) {
        this.isAM = this.matchAM(matches[0]);
      }
    },
    onInput(event) {
      if (!this.disabled) {
        const val = event.target.value;

        this.inputValue = val.slice(0, 11);
        this.showMeridiem = val.length < 10 && !this.is24HourFormat;
      }
    },
    onChange(event) {
      if (!this.disabled) {
        const val = event.target ? event.target.value : event;

        if (moment.isMoment(val)) {
          this.emitModelCompatValue(val);

          return;
        }

        this.setAMOnMatch(val);

        const parsedTime = this.parseTime(val);

        if (moment.isMoment(parsedTime)) {
          const intendsPM =
            this.timeIsAM(parsedTime) &&
            !(this.matchAM(val) || this.isAM || this.is24HourFormat || this.is12based(parsedTime));

          if (intendsPM) {
            parsedTime.add(this.halfDay);
          }

          this.emitModelCompatValue(parsedTime);
        }
      }
    },
  },
};
</script>
